/**
 * The checks we run before a single byte leaves the phone.
 *
 * Purpose is narrow: stop an upload that is *certain* to be rejected, so a
 * customer on a slow connection does not push 80 MB to be told "we only accept
 * PDFs". It is not a security boundary and it is not the validator — the server
 * re-checks everything, and this file must never reject something the server
 * would have accepted.
 *
 * EVERY LIMIT COMES FROM GET /api/v1/meta. Nothing here is hardcoded, because a
 * hardcoded 50 MB in the client is how you promise an upload the API refuses
 * (or, worse, block one it would have taken).
 */
import { formatExtensionList, formatFileSize, formatMegabytes } from '@/lib/format';
import type { UploadLimits } from '@/lib/api/types';

export type PrecheckResult = { ok: true } | { ok: false; code: PrecheckCode; message: string };

export type PrecheckCode = 'empty_file' | 'wrong_type' | 'too_large';

/** 1 MB = 1024 * 1024 bytes, the same sense the backend's MAX_UPLOAD_MB uses. */
export const BYTES_PER_MB = 1024 * 1024;

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

/**
 * Checks one file against the server's advertised limits.
 *
 * `limits` is optional: when meta has not loaded (or failed), we let the file
 * through and let the server decide. Blocking an upload because a *metadata*
 * call failed would be a worse outcome than a wasted request.
 */
export function precheckFile(file: File, limits: UploadLimits | undefined): PrecheckResult {
  if (file.size === 0) {
    return {
      ok: false,
      code: 'empty_file',
      message: 'That file is empty. Please choose the PDF you want printed.',
    };
  }

  if (!limits) return { ok: true };

  const extension = extensionOf(file.name);
  const extensionOk = limits.accepted_extensions.some(
    (accepted) => accepted.toLowerCase() === extension,
  );
  // Some Android file pickers hand over an empty `type`; treat that as "unknown"
  // and fall back to the extension rather than rejecting a perfectly good PDF.
  const mimeOk = file.type === '' || limits.accepted_mime_types.includes(file.type);

  if (!extensionOk || !mimeOk) {
    return {
      ok: false,
      code: 'wrong_type',
      message: `We can only print ${formatExtensionList(limits.accepted_extensions)} files. Please choose a ${formatExtensionList(limits.accepted_extensions)} and try again.`,
    };
  }

  const maxBytes = limits.max_upload_mb * BYTES_PER_MB;
  if (file.size > maxBytes) {
    return {
      ok: false,
      code: 'too_large',
      message: `That file is ${formatFileSize(file.size)}, and the most we can accept is ${formatMegabytes(limits.max_upload_mb)}. Try splitting it into parts and ordering them separately.`,
    };
  }

  return { ok: true };
}
